import { IGGAPApplicationPDF } from '../../models/interfaces/ggap-application-pdf.interface';
import { AI_API_ACCESS_TOKEN } from '@aca-new/app/shared/constants/ai-api-access-token.constants';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { IMyWindow } from '@aca-new/app/shared/interfaces/my-window.interface';
import { ECookieKeys } from '@aca-new/app/shared/models/enums/cookie-keys.enum';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { ETargetType } from '@aca-new/app/shared/models/enums/target-type.enum';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { AppOverlayService } from '@aca-new/app/shared/services/modal-services/app-overlay/app-overlay.service';
import { CookieStorageService } from '@aca-new/app/shared/services/storage-services/cookie-storage/cookie-storage.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { StatusCodes } from 'http-status-codes';
import moment from 'moment-timezone';
import { Observable, of, Subject } from 'rxjs';

// TODO merge with app-file
@Injectable({
  providedIn: 'root',
})
export class AppFileDownloadService {
  public constructor(
    @Inject(WINDOW) private readonly _window: IMyWindow,
    private readonly _cookieStorage: CookieStorageService,
    private readonly _storageService: StorageService,
    private readonly _overlayService: AppOverlayService,
    private readonly _httpService: HttpService
  ) {}

  public downloadBase64(fileName: string, base64: string): void {
    const url = `data:application/octet-stream;base64,${base64}`;

    this.downloadFile(fileName, url);
  }

  public downloadFileDirectlyByFileId(fileName: string, fileId: string): void {
    const url = `${SERVER_URL}/file/download/file-id/${fileId}?fileName=${encodeURIComponent(fileName)}`;

    this.downloadFile(fileName, url);
  }

  public downloadByFileId(fileName: string, fileId: string, specialUserId: string = ''): void {
    this._overlayService.updateIsLoading(true);
    const userId = specialUserId || this._storageService.getItem(EStorageKeys.USER_ID);
    let url = `${SERVER_URL}/user/${userId}/file/${fileId}`;

    if (fileName) {
      url += `?fileName=${fileName}`;
    }

    this.downloadFile(fileName, url);
    this._overlayService.updateIsLoading(false);
  }

  /**
   * Session auth values used for Public API user direct-download URLs.
   * @returns {{ code: string; sessionId: string; userId: string }} Token code, session id, and user id.
   */
  public getUserDirectDownloadAuth(): { code: string; sessionId: string; userId: string } {
    const token = this._cookieStorage.get(ECookieKeys.AUTH_TOKEN) ?? '';

    return {
      code: token.substring(token.length - 50),
      sessionId: this._cookieStorage.get(ECookieKeys.SESSION_ID) ?? '',
      userId: this._storageService.getItem(EStorageKeys.USER_ID),
    };
  }

  /**
   * Triggers a direct file download via anchor navigation.
   * @param {string} fileName - Suggested download file name.
   * @param {string} fileId - File id used for the default user file URL when customUrl is omitted.
   * @param {string} [customUrl] - Full download URL; when set, fileId is not used to build the URL.
   * @returns {void}
   */
  public downloadDirectly(fileName: string, fileId: string, customUrl?: string): void {
    let url = customUrl;

    if (!url) {
      const { code, sessionId, userId } = this.getUserDirectDownloadAuth();

      url = `${SERVER_URL}/user/${userId}/file/${fileId}/session/${sessionId}?code=${code}`;
    }

    this.downloadFile(fileName, url);
  }

  public base64ToArrayBuffer(base64: string): Uint8Array {
    const binaryString: string = this._window.atob(base64);
    const binaryLen: number = binaryString.length;
    const bytes: Uint8Array = new Uint8Array(binaryLen);

    for (let i = 0; i < binaryLen; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }

  public fetchBlob(arrayBuffer: ArrayBuffer, fileName: string): void {
    const finalBlob: Blob = new Blob([arrayBuffer], {
      type: 'octet/stream',
    });
    const url = URL.createObjectURL(finalBlob);

    this.downloadFile(fileName, url);
  }

  public downloadViaBlob$(url: string, zipName: string | null, params: string[], headers?: { [key: string]: string | null }): Observable<boolean> {
    this._overlayService.updateIsLoading(true);
    let contentType: string = 'application/octet-stream';
    const isPostMethod: boolean = params.length > 0;

    if (isPostMethod) {
      contentType = 'application/json';
    }

    const xhr: XMLHttpRequest = new XMLHttpRequest();
    const token = this._cookieStorage.get(ECookieKeys.AUTH_TOKEN);

    xhr.open(isPostMethod ? 'POST' : 'GET', url, true);
    xhr.responseType = 'arraybuffer';

    // Set customized headers, override default ones if exists
    const requestHeaders = Object.assign(
      {
        'authorization': `Bearer ${token}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'ai-api-access-token': AI_API_ACCESS_TOKEN,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': contentType,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Disposition': `attachment; filename="${zipName}"`,
      },
      headers ?? {}
    );

    Object.entries(requestHeaders).forEach(([key, value]): void => {
      if (value) {
        xhr.setRequestHeader(key, value);
      }
    });

    const subject$ = new Subject<boolean>();
    const completeRequest = (isSuccess: boolean): void => {
      subject$.next(isSuccess);
      subject$.complete();
      this._overlayService.updateIsLoading(false);
    };

    xhr.onload = (): void => {
      if (xhr.status === StatusCodes.OK) {
        this.fetchBlob(xhr.response as ArrayBuffer, zipName || '');
        completeRequest(true);

        return;
      }

      completeRequest(false);
    };

    xhr.onerror = (): void => {
      completeRequest(false);
    };

    if (isPostMethod) {
      xhr.send(JSON.stringify(params));
    } else {
      xhr.send();
    }

    return subject$;
  }

  public downloadFilesByZip$(
    ids: string,
    orderNo: string,
    fileName: string | null,
    isPost?: boolean,
    headers?: { [key: string]: string | null },
    overrideUserId?: string
  ): Observable<boolean> {
    const userId = overrideUserId ?? this._storageService.getItem(EStorageKeys.USER_ID);
    let zipName: string | null = fileName;

    // multiple files should use zip suffix
    if (ids.indexOf(',') > -1) {
      zipName = fileName || `${orderNo} Attachments ${moment().format('DD-MMM-YYYY')}.zip`;
    }

    let params: string[] = [];
    let url: string = `${SERVER_URL}/user/${userId}/files-in-zip/${ids}`;

    if (isPost) {
      url = `${SERVER_URL}/user/${userId}/files-in-zip/`;
      params = ids.split(',');
    }

    return this.downloadViaBlob$(url, zipName, params, headers);
  }

  public downloadFile(fileName: string, url: string, target: ETargetType = ETargetType.BLANK): void {
    if (!url) {
      return;
    }

    const a = document.createElement('a');

    a.id = 'anchor-for-download-file';
    document.body.appendChild(a);
    a.className = 'downloader';
    a.target = target;

    a.href = url;
    a.download = fileName;
    a.click();
    document.getElementById('anchor-for-download-file')?.remove();
  }

  public downloadFileByBlob(blob: Blob, filename: string): void {
    const link = document.createElement('a');

    link.href = this._window.URL.createObjectURL(blob);
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this._window.URL.revokeObjectURL(link.href);
  }

  public downloadApplicationPdfByFileId(fileId: string): void {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);
    const url = `${SERVER_URL}/user/${userId}/file/${fileId}`;

    return this.downloadFile('application.pdf', url);
  }

  public getApplicationPdfFileId$(orderId: string): Observable<HttpResponse<IGGAPApplicationPDF>> {
    if (!orderId) {
      return of(new HttpResponse<IGGAPApplicationPDF>({ body: { message: '', content: [] } }));
    }

    const userId = this._storageService.getItem(EStorageKeys.USER_ID);
    const url = `${SERVER_URL}/user/${userId}/order/${orderId}/file/application`;

    return this._httpService.httpClient.get<IGGAPApplicationPDF>(url, { observe: 'response' });
  }

  public generateApplicationDocuments$(orderId: string): Observable<string> {
    const url = `${SERVER_URL}/food-certification-services-api/order/${orderId}/application-document`;

    return this._httpService.httpClient.post<string>(url, { observe: 'response' });
  }
}
